import { Actions, createEffect, ofType } from '@ngrx/effects';

import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

import { Config, MW_APP_CONFIG } from '../../../config';
import { MwUserApiService } from '@core/auth/services/user-api.service';
import { MwUserService } from '@core/auth/services/user.service';
import { MwAuthService } from '../../services/auth.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { userActions } from '../user';
import * as actions from './oidc.actions';

interface UserData {
  selectedLanguage: string;
  selectedCompanyId: number;
  lastActiveCompanyId: number;
  companyNotRequired: boolean;
  rawUserData: any;
}
/**
 * Authorization process:
 * 1. Check if user is authenticated (action: checkAuth)
 * 2. If not - redirect user to STS Login page (action: authorize)
 * 3. If user is authenticated - receive user data from STS (action: authorizationSucceeded)
 * 4. If user data received - try to set active company (action: trySetActiveCompany)
 * 5. If active company set - set user profile (action: setUserProfile)
 */
@Injectable()
export class OIDCEffects {
  // First step (Always) - user authentication
  checkAuth$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.checkAuth),
      switchMap(() => this.authService.checkAuth()),
      switchMap((loginResponse) =>
        loginResponse?.isAuthenticated
          ? this.authService
              .getPayloadFromAccessToken()
              .pipe(
                map(
                  (payload) =>
                    new Date(payload?.exp * 1000).getTime() >
                    new Date().getTime()
                )
              )
          : of(false)
      ),
      map((isAuthenticated) =>
        isAuthenticated ? actions.authorizationSucceeded() : actions.authorize()
      )
    )
  );

  // Second step (if user is not authenticated) - user authorization
  authorize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.authorize),
        map(() => window.location.href),
        filter((url) => this.isAuthorizeUrl(url)),
        tap((url) => {
          LocalStorageService.saveStoredRedirectRoute(url.split('#')[1] || '');
          // Will redirect user to STS Login page
          this.authService.authorize();
        })
      ),
    { dispatch: false }
  );

  // Third step (if user authorized)- user data receive
  authorizationSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.authorizationSucceeded),
      switchMap(() => this.authService.userData$),
      filter((userData) => userData),
      switchMap((userData) => [actions.userDataReceived(userData)])
    )
  );

  // Fourth step (if user data from form STS received) - try to set active company
  oidcUserDataReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.userDataReceived),
      switchMap(this.passIfLoggedIn.bind(this)),
      switchMap(this.passIfLinkedWithMobileWorkerAccount.bind(this)),
      map((t) => this.buildUserData(t)),
      map((t) => userActions.trySetActiveCompany(t))
    )
  );

  configLoaded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.configLoaded),
      map(() => actions.checkAuth())
    )
  );

  setUserProfile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(userActions.setUserProfile),
        tap((action) =>
          // Navigation also is done in setActiveCompanyById (user.service.ts)
          // if stored route is not set or user has no access to module
          LocalStorageService.tryToRestoreRoute(
            this.config,
            this.router,
            action.userApiProfile?.hasAccessToModule === true
          )
        )
      ),
    { dispatch: false }
  );

  logInByGuid$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.logInByGuid),
        tap((action) => {
          this.authService.authorizeByGuid(action.guid);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly actions$: Actions,
    private readonly authService: MwAuthService,
    private readonly userApiService: MwUserApiService,
    private readonly userService: MwUserService,
    private readonly router: Router,
    @Inject(MW_APP_CONFIG) private config: Config
  ) {}

  private passIfLoggedIn(rawData: any): Observable<any> {
    return this.authService.loggedIn$.pipe(
      filter((loggedIn) => loggedIn),
      take(1),
      map(() => rawData)
    );
  }

  private passIfLinkedWithMobileWorkerAccount(rawData: any): Observable<any> {
    return this.userApiService.hasLinkWithMobileWorkerAccount().pipe(
      tap((isLinked) => {
        if (!isLinked) {
          this.userService.refreshUserProfile();
          this.router.navigate(['user-profile']);
        }
      }),
      filter((isLinked) => isLinked),
      map(() => rawData)
    );
  }

  private buildUserData(rawData: any): UserData {
    return {
      selectedLanguage: rawData['mw-language'],
      selectedCompanyId: +rawData['mw-company-id'],
      lastActiveCompanyId: LocalStorageService.getLastActiveCompanyId(),
      companyNotRequired: rawData['mw-company-not-required'] === 'true',
      rawUserData: rawData,
    };
  }
  private isAuthorizeUrl(url: string): boolean {
    const skipRoutes = ['unauthorized', 'autologin'].concat(
      this.config.environment.skipAuthRoutes || []
    );
    const skip = skipRoutes.some((skipRoute) => url.includes(skipRoute));

    return !skip;
  }
}
